import "@/App.css";
import "@/i18n";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/contexts/AuthContext";
import { FavoritesProvider } from "@/contexts/FavoritesContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { SUPPORTED_LANGUAGES } from "@/i18n";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import HomePage from "@/pages/HomePage";
import SportPage from "@/pages/SportPage";
import LeaguePage from "@/pages/LeaguePage";
import MatchDetailPage from "@/pages/MatchDetailPage";
import ValueBetsPage from "@/pages/ValueBetsPage";
import SurebetsPage from "@/pages/SurebetsPage";
import OddsMovementsPage from "@/pages/OddsMovementsPage";
import BookmakerPage from "@/pages/BookmakerPage";
import SearchResultsPage from "@/pages/SearchResultsPage";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import MappingQueue from "@/pages/admin/MappingQueue";
import BookmakerManagement from "@/pages/admin/BookmakerManagement";
import JobsStatus from "@/pages/admin/JobsStatus";
import LogsPage from "@/pages/admin/LogsPage";
import CacheTools from "@/pages/admin/CacheTools";
import AffiliateDashboard from "@/pages/admin/AffiliateDashboard";
import AlertsPage from "@/pages/admin/AlertsPage";
import SportsManagement from "@/pages/admin/SportsManagement";
import DataQualityPage from "@/pages/admin/DataQualityPage";
import PremiumConfig from "@/pages/admin/PremiumConfig";
import ProvidersPage from "@/pages/admin/ProvidersPage";
import ConfigManagement from "@/pages/admin/ConfigManagement";
import AdminSEODashboard from "@/pages/admin/AdminSEODashboard";
import AdminPromptManager from "@/pages/admin/AdminPromptManager";
import AdminKeywordManager from "@/pages/admin/AdminKeywordManager";
import AdminLanguageSettings from "@/pages/admin/AdminLanguageSettings";
import AdminPagesManager from "@/pages/admin/AdminPagesManager";
import AdminChangePassword from "@/pages/admin/AdminChangePassword";
import AdminBotMonitor from "@/pages/admin/AdminBotMonitor";
import AdminJobManager from "@/pages/admin/AdminJobManager";
import AdminCupsManager from "@/pages/admin/AdminCupsManager";
import SEOOSOverview from "@/modules/seoos/SEOOSOverview";
import { SEOOSLayout } from "@/modules/seoos/SEOOSShared";
import SEOOSTechnical from "@/modules/seoos/SEOOSTechnical";
import SEOOSCrawl from "@/modules/seoos/SEOOSCrawl";
import SEOOSContentQuality from "@/modules/seoos/SEOOSContentQuality";
import SEOOSTasks from "@/modules/seoos/SEOOSTasks";
import SEOOSLinkGraph from "@/modules/seoos/SEOOSLinkGraph";
import SEOOSOpportunities from "@/modules/seoos/SEOOSOpportunities";
import SEOOSWarRoom from "@/modules/seoos/SEOOSWarRoom";
import SEOOSReports from "@/modules/seoos/SEOOSReports";
import SEOOSFixCenter from "@/modules/seoos/SEOOSFixCenter";
import BookmakersPage from "@/pages/BookmakersPage";
import WorldCupHub from "@/pages/WorldCupHub";
import CupsPage from "@/pages/CupsPage";
import CupDetailPage from "@/pages/CupDetailPage";
import SitemapHub from "@/pages/SitemapHub";
import NotFoundPage from "@/pages/NotFoundPage";
import WorldCupGroup from "@/pages/WorldCupGroup";
import WorldCupTurkey from "@/pages/WorldCupTurkey";
import WorldCupFixtures from "@/pages/WorldCupFixtures";
import WorldCupMatch from "@/pages/WorldCupMatch";
import WorldCupTeam from "@/pages/WorldCupTeam";
import WorldCupScores from "@/pages/WorldCupScores";
import WorldCupGroupsPage from "@/pages/WorldCupGroupsPage";
import WorldCupTeamsPage from "@/pages/WorldCupTeamsPage";
import WorldCupHostCountries from "@/pages/WorldCupHostCountries";
import WorldCupCities from "@/pages/WorldCupCities";
import WorldCupStadiums from "@/pages/WorldCupStadiums";
import WorldCupTickets from "@/pages/WorldCupTickets";
import WCHostCountryPage from "@/pages/WCHostCountryPage";
import WCCityPage from "@/pages/WCCityPage";
import WCStadiumPage from "@/pages/WCStadiumPage";
import ProgrammaticPage from "@/pages/ProgrammaticPage";
import TrendPage from "@/pages/TrendPage";
import StaticPage from "@/pages/StaticPage";

function PublicLayout({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-slate-950">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

// RTL wrapper for Arabic
function RTLWrapper({ children }) {
  const location = window.location.pathname;
  const isArabic = location.startsWith('/ar');
  return <div dir={isArabic ? 'rtl' : 'ltr'}>{children}</div>;
}

// Generates all localized path patterns for a route key
const LANG_CODES = SUPPORTED_LANGUAGES.map(l => l.code).join('|');

// Wrapper to detect language-prefixed match detail routes
function LangMatchDetail() {
  const { slug } = useParams();
  return <PublicLayout><MatchDetailPage key={slug} /></PublicLayout>;
}

const VALID_LANGS = new Set(SUPPORTED_LANGUAGES.map(l => l.code));

function LangGuard({ children }) {
  const { lang } = useParams();
  if (!VALID_LANGS.has(lang)) {
    return <PublicLayout><NotFoundPage /></PublicLayout>;
  }
  return children;
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <FavoritesProvider>
        <BrowserRouter>
          <LanguageProvider>
          <RTLWrapper>
          <Routes>
            {/* Root redirects to /tr (primary language) */}
            <Route path="/" element={<Navigate to="/tr" replace />} />

            {/* Catch invalid lang codes at first level */}
            <Route path="/:lang" element={<LangGuard><PublicLayout><HomePage /></PublicLayout></LangGuard>} />
            
            {/* Match detail - all localized paths: /en/match/slug, /tr/mac/slug, /es/partido/slug, etc. */}
            <Route path="/:lang/match/:slug" element={<LangMatchDetail />} />
            <Route path="/:lang/mac/:slug" element={<LangMatchDetail />} />
            <Route path="/:lang/partido/:slug" element={<LangMatchDetail />} />
            <Route path="/:lang/jogo/:slug" element={<LangMatchDetail />} />
            <Route path="/:lang/spiel/:slug" element={<LangMatchDetail />} />
            <Route path="/:lang/partita/:slug" element={<LangMatchDetail />} />
            <Route path="/:lang/wedstrijd/:slug" element={<LangMatchDetail />} />
            <Route path="/:lang/mubarat/:slug" element={<LangMatchDetail />} />
            <Route path="/:lang/pertandingan/:slug" element={<LangMatchDetail />} />

            {/* League - localized */}
            <Route path="/:lang/league/:slug" element={<PublicLayout><LeaguePage /></PublicLayout>} />
            <Route path="/:lang/lig/:slug" element={<PublicLayout><LeaguePage /></PublicLayout>} />
            <Route path="/:lang/liga/:slug" element={<PublicLayout><LeaguePage /></PublicLayout>} />
            <Route path="/:lang/ligue/:slug" element={<PublicLayout><LeaguePage /></PublicLayout>} />
            <Route path="/:lang/campionato/:slug" element={<PublicLayout><LeaguePage /></PublicLayout>} />
            <Route path="/:lang/competitie/:slug" element={<PublicLayout><LeaguePage /></PublicLayout>} />
            <Route path="/:lang/dawri/:slug" element={<PublicLayout><LeaguePage /></PublicLayout>} />

            {/* Bookmaker - localized */}
            <Route path="/:lang/bookmaker/:slug" element={<PublicLayout><BookmakerPage /></PublicLayout>} />
            <Route path="/:lang/bahisci/:slug" element={<PublicLayout><BookmakerPage /></PublicLayout>} />
            <Route path="/:lang/casa-apuestas/:slug" element={<PublicLayout><BookmakerPage /></PublicLayout>} />
            <Route path="/:lang/casa-apostas/:slug" element={<PublicLayout><BookmakerPage /></PublicLayout>} />
            <Route path="/:lang/buchmacher/:slug" element={<PublicLayout><BookmakerPage /></PublicLayout>} />
            <Route path="/:lang/bukmeker/:slug" element={<PublicLayout><BookmakerPage /></PublicLayout>} />
            <Route path="/:lang/murahan/:slug" element={<PublicLayout><BookmakerPage /></PublicLayout>} />
            <Route path="/:lang/bandar/:slug" element={<PublicLayout><BookmakerPage /></PublicLayout>} />

            {/* Football - explicit localized routes */}
            <Route path="/:lang/futbol" element={<PublicLayout><SportPage /></PublicLayout>} />
            <Route path="/:lang/football" element={<PublicLayout><SportPage /></PublicLayout>} />
            <Route path="/:lang/fussball" element={<PublicLayout><SportPage /></PublicLayout>} />

            {/* World Cup 2026 */}
            <Route path="/:lang/dunya-kupasi-2026" element={<PublicLayout><WorldCupHub /></PublicLayout>} />
            <Route path="/:lang/world-cup-2026" element={<PublicLayout><WorldCupHub /></PublicLayout>} />
            <Route path="/:lang/dunya-kupasi-2026/turkiye" element={<PublicLayout><WorldCupTurkey /></PublicLayout>} />
            <Route path="/:lang/world-cup-2026/turkey" element={<PublicLayout><WorldCupTurkey /></PublicLayout>} />
            <Route path="/:lang/dunya-kupasi-2026/fikstur" element={<PublicLayout><WorldCupFixtures /></PublicLayout>} />
            <Route path="/:lang/world-cup-2026/fixtures" element={<PublicLayout><WorldCupFixtures /></PublicLayout>} />
            <Route path="/:lang/dunya-kupasi-2026/mac/:matchId" element={<PublicLayout><WorldCupMatch /></PublicLayout>} />
            <Route path="/:lang/world-cup-2026/match/:matchId" element={<PublicLayout><WorldCupMatch /></PublicLayout>} />
            <Route path="/:lang/dunya-kupasi-2026/grup/:id" element={<PublicLayout><WorldCupGroup /></PublicLayout>} />
            <Route path="/:lang/world-cup-2026/group/:id" element={<PublicLayout><WorldCupGroup /></PublicLayout>} />
            <Route path="/:lang/dunya-kupasi-2026/takim/:slug" element={<PublicLayout><WorldCupTeam /></PublicLayout>} />
            <Route path="/:lang/world-cup-2026/team/:slug" element={<PublicLayout><WorldCupTeam /></PublicLayout>} />

            {/* World Cup 2026 - New VIP Pages */}
            <Route path="/:lang/dunya-kupasi-2026/skor-ve-fikstur" element={<PublicLayout><WorldCupScores /></PublicLayout>} />
            <Route path="/:lang/world-cup-2026/scores" element={<PublicLayout><WorldCupScores /></PublicLayout>} />
            <Route path="/:lang/dunya-kupasi-2026/gruplar" element={<PublicLayout><WorldCupGroupsPage /></PublicLayout>} />
            <Route path="/:lang/world-cup-2026/groups" element={<PublicLayout><WorldCupGroupsPage /></PublicLayout>} />
            <Route path="/:lang/dunya-kupasi-2026/takimlar" element={<PublicLayout><WorldCupTeamsPage /></PublicLayout>} />
            <Route path="/:lang/world-cup-2026/teams" element={<PublicLayout><WorldCupTeamsPage /></PublicLayout>} />
            <Route path="/:lang/dunya-kupasi-2026/ev-sahibi-ulkeler" element={<PublicLayout><WorldCupHostCountries /></PublicLayout>} />
            <Route path="/:lang/world-cup-2026/host-countries" element={<PublicLayout><WorldCupHostCountries /></PublicLayout>} />
            <Route path="/:lang/dunya-kupasi-2026/sehirler" element={<PublicLayout><WorldCupCities /></PublicLayout>} />
            <Route path="/:lang/world-cup-2026/cities" element={<PublicLayout><WorldCupCities /></PublicLayout>} />
            <Route path="/:lang/dunya-kupasi-2026/stadyumlar" element={<PublicLayout><WorldCupStadiums /></PublicLayout>} />
            <Route path="/:lang/world-cup-2026/stadiums" element={<PublicLayout><WorldCupStadiums /></PublicLayout>} />
            <Route path="/:lang/dunya-kupasi-2026/bilet-satis" element={<PublicLayout><WorldCupTickets /></PublicLayout>} />
            <Route path="/:lang/world-cup-2026/tickets" element={<PublicLayout><WorldCupTickets /></PublicLayout>} />

            {/* World Cup 2026 - Host Country MEGA Module (TR: ev-sahibi/abd, EN: host/usa) */}
            <Route path="/:lang/dunya-kupasi-2026/ev-sahibi/:slug" element={<PublicLayout><WCHostCountryPage /></PublicLayout>} />
            <Route path="/:lang/dunya-kupasi-2026/host/:slug" element={<PublicLayout><WCHostCountryPage /></PublicLayout>} />
            <Route path="/:lang/world-cup-2026/host/:slug" element={<PublicLayout><WCHostCountryPage /></PublicLayout>} />
            <Route path="/:lang/dunya-kupasi-2026/ev-sahibi/:country/:citySlug" element={<PublicLayout><WCCityPage /></PublicLayout>} />
            <Route path="/:lang/dunya-kupasi-2026/host/:country/:citySlug" element={<PublicLayout><WCCityPage /></PublicLayout>} />
            <Route path="/:lang/world-cup-2026/host/:country/:citySlug" element={<PublicLayout><WCCityPage /></PublicLayout>} />
            <Route path="/:lang/dunya-kupasi-2026/stadyum/:slug" element={<PublicLayout><WCStadiumPage /></PublicLayout>} />
            <Route path="/:lang/dunya-kupasi-2026/stadium/:slug" element={<PublicLayout><WCStadiumPage /></PublicLayout>} />
            <Route path="/:lang/world-cup-2026/stadium/:slug" element={<PublicLayout><WCStadiumPage /></PublicLayout>} />

            {/* All Cups & Tournaments */}
            <Route path="/:lang/cups" element={<PublicLayout><CupsPage /></PublicLayout>} />
            <Route path="/:lang/kupalar" element={<PublicLayout><CupsPage /></PublicLayout>} />
            <Route path="/:lang/copas" element={<PublicLayout><CupsPage /></PublicLayout>} />
            <Route path="/:lang/pokale" element={<PublicLayout><CupsPage /></PublicLayout>} />
            <Route path="/:lang/coupes" element={<PublicLayout><CupsPage /></PublicLayout>} />
            <Route path="/:lang/coppe" element={<PublicLayout><CupsPage /></PublicLayout>} />

            {/* Cup Detail */}
            <Route path="/:lang/cup/:slug" element={<PublicLayout><CupDetailPage /></PublicLayout>} />
            <Route path="/:lang/kupa/:slug" element={<PublicLayout><CupDetailPage /></PublicLayout>} />

            {/* HTML Sitemap Hub */}
            <Route path="/:lang/site-map" element={<PublicLayout><SitemapHub /></PublicLayout>} />
            <Route path="/:lang/seo-hub" element={<PublicLayout><SitemapHub /></PublicLayout>} />

            {/* Value Bets - localized */}
            <Route path="/:lang/value-bets" element={<PublicLayout><ValueBetsPage /></PublicLayout>} />
            <Route path="/:lang/deger-bahisleri" element={<PublicLayout><ValueBetsPage /></PublicLayout>} />
            <Route path="/:lang/apuestas-valor" element={<PublicLayout><ValueBetsPage /></PublicLayout>} />
            <Route path="/:lang/apostas-valor" element={<PublicLayout><ValueBetsPage /></PublicLayout>} />
            <Route path="/:lang/wertquoten" element={<PublicLayout><ValueBetsPage /></PublicLayout>} />
            <Route path="/:lang/paris-valeur" element={<PublicLayout><ValueBetsPage /></PublicLayout>} />
            <Route path="/:lang/scommesse-valore" element={<PublicLayout><ValueBetsPage /></PublicLayout>} />
            <Route path="/:lang/waarde-weddenschappen" element={<PublicLayout><ValueBetsPage /></PublicLayout>} />
            <Route path="/:lang/stavki-cennost" element={<PublicLayout><ValueBetsPage /></PublicLayout>} />
            <Route path="/:lang/tsinnisni-stavky" element={<PublicLayout><ValueBetsPage /></PublicLayout>} />
            <Route path="/:lang/rahanat-qiyma" element={<PublicLayout><ValueBetsPage /></PublicLayout>} />
            <Route path="/:lang/taruhan-nilai" element={<PublicLayout><ValueBetsPage /></PublicLayout>} />

            {/* Surebets - localized */}
            <Route path="/:lang/surebets" element={<PublicLayout><SurebetsPage /></PublicLayout>} />
            <Route path="/:lang/kesin-bahisler" element={<PublicLayout><SurebetsPage /></PublicLayout>} />
            <Route path="/:lang/apuestas-seguras" element={<PublicLayout><SurebetsPage /></PublicLayout>} />
            <Route path="/:lang/apostas-seguras" element={<PublicLayout><SurebetsPage /></PublicLayout>} />
            <Route path="/:lang/sichere-wetten" element={<PublicLayout><SurebetsPage /></PublicLayout>} />
            <Route path="/:lang/paris-surs" element={<PublicLayout><SurebetsPage /></PublicLayout>} />
            <Route path="/:lang/scommesse-sicure" element={<PublicLayout><SurebetsPage /></PublicLayout>} />
            <Route path="/:lang/zekere-weddenschappen" element={<PublicLayout><SurebetsPage /></PublicLayout>} />
            <Route path="/:lang/vernye-stavki" element={<PublicLayout><SurebetsPage /></PublicLayout>} />
            <Route path="/:lang/virni-stavky" element={<PublicLayout><SurebetsPage /></PublicLayout>} />
            <Route path="/:lang/rahanat-akida" element={<PublicLayout><SurebetsPage /></PublicLayout>} />
            <Route path="/:lang/taruhan-pasti" element={<PublicLayout><SurebetsPage /></PublicLayout>} />

            {/* Odds Movements - localized */}
            <Route path="/:lang/odds-movements" element={<PublicLayout><OddsMovementsPage /></PublicLayout>} />
            <Route path="/:lang/oran-hareketleri" element={<PublicLayout><OddsMovementsPage /></PublicLayout>} />
            <Route path="/:lang/movimientos-cuotas" element={<PublicLayout><OddsMovementsPage /></PublicLayout>} />
            <Route path="/:lang/movimentos-odds" element={<PublicLayout><OddsMovementsPage /></PublicLayout>} />
            <Route path="/:lang/quotenbewegungen" element={<PublicLayout><OddsMovementsPage /></PublicLayout>} />
            <Route path="/:lang/mouvements-cotes" element={<PublicLayout><OddsMovementsPage /></PublicLayout>} />
            <Route path="/:lang/movimenti-quote" element={<PublicLayout><OddsMovementsPage /></PublicLayout>} />
            <Route path="/:lang/odds-bewegingen" element={<PublicLayout><OddsMovementsPage /></PublicLayout>} />
            <Route path="/:lang/dvizhenie-koeffitsientov" element={<PublicLayout><OddsMovementsPage /></PublicLayout>} />
            <Route path="/:lang/rukh-koefitsiyentiv" element={<PublicLayout><OddsMovementsPage /></PublicLayout>} />
            <Route path="/:lang/harakat-ihtimalat" element={<PublicLayout><OddsMovementsPage /></PublicLayout>} />
            <Route path="/:lang/pergerakan-odds" element={<PublicLayout><OddsMovementsPage /></PublicLayout>} />

            {/* Search */}
            <Route path="/:lang/search" element={<PublicLayout><SearchResultsPage /></PublicLayout>} />

            {/* Bookmakers listing */}
            <Route path="/:lang/bookmakers" element={<PublicLayout><BookmakersPage /></PublicLayout>} />

            {/* Programmatic SEO Pages */}
            <Route path="/:lang/best-odds" element={<PublicLayout><ProgrammaticPage pageType="best_odds" /></PublicLayout>} />
            <Route path="/:lang/live-odds" element={<PublicLayout><ProgrammaticPage pageType="live_odds" /></PublicLayout>} />
            <Route path="/:lang/football-predictions" element={<PublicLayout><ProgrammaticPage pageType="football_predictions" /></PublicLayout>} />
            <Route path="/:lang/odds-comparison" element={<PublicLayout><ProgrammaticPage pageType="odds_comparison" /></PublicLayout>} />
            <Route path="/:lang/over-2-5-trends" element={<PublicLayout><ProgrammaticPage pageType="over_2_5_trends" /></PublicLayout>} />
            <Route path="/:lang/btts-trends" element={<PublicLayout><ProgrammaticPage pageType="btts_trends" /></PublicLayout>} />
            <Route path="/:lang/first-half-trends" element={<PublicLayout><ProgrammaticPage pageType="first_half_trends" /></PublicLayout>} />

            {/* Trend Pages */}
            <Route path="/:lang/trends/over-2-5" element={<PublicLayout><TrendPage trendType="over_2_5" /></PublicLayout>} />
            <Route path="/:lang/trends/btts" element={<PublicLayout><TrendPage trendType="btts" /></PublicLayout>} />
            <Route path="/:lang/trends/first-half-goals" element={<PublicLayout><TrendPage trendType="first_half_goals" /></PublicLayout>} />
            <Route path="/:lang/trends/corners" element={<PublicLayout><TrendPage trendType="corners" /></PublicLayout>} />
            <Route path="/:lang/trends/cards" element={<PublicLayout><TrendPage trendType="cards" /></PublicLayout>} />

            {/* Static/Corporate Pages - About with localized slugs */}
            <Route path="/:lang/about" element={<PublicLayout><StaticPage pageSlug="about" /></PublicLayout>} />
            <Route path="/:lang/hakkimizda" element={<PublicLayout><StaticPage pageSlug="about" /></PublicLayout>} />
            <Route path="/:lang/sobre-nosotros" element={<PublicLayout><StaticPage pageSlug="about" /></PublicLayout>} />
            <Route path="/:lang/uber-uns" element={<PublicLayout><StaticPage pageSlug="about" /></PublicLayout>} />
            <Route path="/:lang/a-propos" element={<PublicLayout><StaticPage pageSlug="about" /></PublicLayout>} />
            <Route path="/:lang/chi-siamo" element={<PublicLayout><StaticPage pageSlug="about" /></PublicLayout>} />
            <Route path="/:lang/over-ons" element={<PublicLayout><StaticPage pageSlug="about" /></PublicLayout>} />
            <Route path="/:lang/sobre-nos" element={<PublicLayout><StaticPage pageSlug="about" /></PublicLayout>} />
            <Route path="/:lang/o-nas" element={<PublicLayout><StaticPage pageSlug="about" /></PublicLayout>} />
            <Route path="/:lang/pro-nas" element={<PublicLayout><StaticPage pageSlug="about" /></PublicLayout>} />
            <Route path="/:lang/tentang-kami" element={<PublicLayout><StaticPage pageSlug="about" /></PublicLayout>} />
            <Route path="/:lang/contact" element={<PublicLayout><StaticPage pageSlug="contact" /></PublicLayout>} />
            <Route path="/:lang/privacy-policy" element={<PublicLayout><StaticPage pageSlug="privacy-policy" /></PublicLayout>} />
            <Route path="/:lang/terms-of-service" element={<PublicLayout><StaticPage pageSlug="terms-of-service" /></PublicLayout>} />
            <Route path="/:lang/responsible-gambling" element={<PublicLayout><StaticPage pageSlug="responsible-gambling" /></PublicLayout>} />
            <Route path="/:lang/sorumlu-bahis" element={<PublicLayout><StaticPage pageSlug="sorumlu-bahis" /></PublicLayout>} />
            <Route path="/:lang/disclaimer" element={<PublicLayout><StaticPage pageSlug="disclaimer" /></PublicLayout>} />
            <Route path="/:lang/affiliate-disclosure" element={<PublicLayout><StaticPage pageSlug="affiliate-disclosure" /></PublicLayout>} />
            <Route path="/:lang/how-odds-work" element={<PublicLayout><StaticPage pageSlug="how-odds-work" /></PublicLayout>} />
            <Route path="/:lang/value-bet-methodology" element={<PublicLayout><StaticPage pageSlug="value-bet-methodology" /></PublicLayout>} />
            <Route path="/:lang/data-sources" element={<PublicLayout><StaticPage pageSlug="data-sources" /></PublicLayout>} />
            <Route path="/:lang/bookmaker-reviews" element={<PublicLayout><StaticPage pageSlug="bookmaker-reviews" /></PublicLayout>} />
            <Route path="/:lang/faq" element={<PublicLayout><StaticPage pageSlug="faq" /></PublicLayout>} />
            <Route path="/:lang/value-bet-guide" element={<PublicLayout><StaticPage pageSlug="value-bet-guide" /></PublicLayout>} />
            <Route path="/:lang/how-arbitrage-betting-works" element={<PublicLayout><StaticPage pageSlug="how-arbitrage-betting-works" /></PublicLayout>} />
            <Route path="/:lang/betting-strategies" element={<PublicLayout><StaticPage pageSlug="betting-strategies" /></PublicLayout>} />
            <Route path="/:lang/best-betting-sites" element={<PublicLayout><StaticPage pageSlug="best-betting-sites" /></PublicLayout>} />
            <Route path="/:lang/betting-site-comparison" element={<PublicLayout><StaticPage pageSlug="betting-site-comparison" /></PublicLayout>} />
            <Route path="/:lang/best-odds-bookmakers" element={<PublicLayout><StaticPage pageSlug="best-odds-bookmakers" /></PublicLayout>} />
            <Route path="/:lang/football-betting-guide" element={<PublicLayout><StaticPage pageSlug="football-betting-guide" /></PublicLayout>} />
            <Route path="/:lang/sports-betting-tips" element={<PublicLayout><StaticPage pageSlug="sports-betting-tips" /></PublicLayout>} />
            <Route path="/:lang/how-to-read-betting-odds" element={<PublicLayout><StaticPage pageSlug="how-to-read-betting-odds" /></PublicLayout>} />

            {/* Sport & League (non-localized path segments, uses sport/league slugs) */}
            <Route path="/:lang/:sport" element={<PublicLayout><SportPage /></PublicLayout>} />
            <Route path="/:lang/:sport/:league" element={<PublicLayout><LeaguePage /></PublicLayout>} />

            {/* Legacy non-prefixed routes - redirect to /en */}
            <Route path="/match/:slug" element={<LangMatchDetail />} />
            <Route path="/value-bets" element={<PublicLayout><ValueBetsPage /></PublicLayout>} />
            <Route path="/surebets" element={<PublicLayout><SurebetsPage /></PublicLayout>} />
            <Route path="/odds-movements" element={<PublicLayout><OddsMovementsPage /></PublicLayout>} />
            <Route path="/bookmaker/:slug" element={<PublicLayout><BookmakerPage /></PublicLayout>} />
            <Route path="/search" element={<PublicLayout><SearchResultsPage /></PublicLayout>} />
            <Route path="/:sport" element={<PublicLayout><SportPage /></PublicLayout>} />
            <Route path="/:sport/:league" element={<PublicLayout><LeaguePage /></PublicLayout>} />

            {/* Admin routes (no language prefix) */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="mapping" element={<MappingQueue />} />
              <Route path="bookmakers" element={<BookmakerManagement />} />
              <Route path="affiliate" element={<AffiliateDashboard />} />
              <Route path="alerts" element={<AlertsPage />} />
              <Route path="entities" element={<SportsManagement />} />
              <Route path="data-quality" element={<DataQualityPage />} />
              <Route path="premium" element={<PremiumConfig />} />
              <Route path="providers" element={<ProvidersPage />} />
              <Route path="config" element={<ConfigManagement />} />
              <Route path="seo" element={<AdminSEODashboard />} />
              <Route path="prompts" element={<AdminPromptManager />} />
              <Route path="keywords" element={<AdminKeywordManager />} />
              <Route path="language-settings" element={<AdminLanguageSettings />} />
              <Route path="pages" element={<AdminPagesManager />} />
              <Route path="change-password" element={<AdminChangePassword />} />
              <Route path="bot-monitor" element={<AdminBotMonitor />} />
              <Route path="ai-jobs" element={<AdminJobManager />} />
              <Route path="jobs" element={<JobsStatus />} />
              <Route path="logs" element={<LogsPage />} />
              <Route path="cache" element={<CacheTools />} />
              <Route path="cups" element={<AdminCupsManager />} />
              <Route path="seoos" element={<SEOOSLayout />}>
                <Route index element={<SEOOSOverview />} />
                <Route path="overview" element={<SEOOSOverview />} />
                <Route path="technical" element={<SEOOSTechnical />} />
                <Route path="index-health" element={<SEOOSOverview />} />
                <Route path="crawl" element={<SEOOSCrawl />} />
                <Route path="content-quality" element={<SEOOSContentQuality />} />
                <Route path="link-graph" element={<SEOOSLinkGraph />} />
                <Route path="opportunities" element={<SEOOSOpportunities />} />
                <Route path="war-room" element={<SEOOSWarRoom />} />
                <Route path="reports" element={<SEOOSReports />} />
                <Route path="fix-center" element={<SEOOSFixCenter />} />
                <Route path="tasks" element={<SEOOSTasks />} />
              </Route>
            </Route>

            {/* 404 Catch-all - must be LAST */}
            <Route path="*" element={<PublicLayout><NotFoundPage /></PublicLayout>} />
          </Routes>
          </RTLWrapper>
          </LanguageProvider>
        </BrowserRouter>
        </FavoritesProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
